import { useEffect, useState } from 'react';
import { Box, FormField, Multiselect, SpaceBetween, Textarea } from '@cloudscape-design/components';
import type { createApiClient } from '@deloitte-us-consulting/gp-sdk';
import type { AvailableSystem } from './types';

type Api = ReturnType<typeof createApiClient>;

/** The form error for the data section, or undefined when it is fine. */
export function dataSectionError(selected: string[], need: string): string | undefined {
  return selected.length > 0 && !need.trim() ? 'Say what the app needs the data for.' : undefined;
}

interface Props {
  api: Api;
  selected: string[];
  need: string;
  onChange: (selected: string[], need: string) => void;
}

/**
 * Which data systems the app wants, by name only. Tables and fields are not chosen here:
 * after submitting, the platform team agrees them with the builder in a short call (the
 * data review), which keeps the form short and the result accurate. The sample data in
 * the app template shows what each system holds.
 */
export default function DataSystemsPicker({ api, selected, need, onChange }: Props) {
  const [systems, setSystems] = useState<AvailableSystem[] | null>(null);
  useEffect(() => {
    api
      .get<AvailableSystem[]>('/api/catalog/available-systems')
      .then(setSystems)
      .catch(() => setSystems([]));
  }, [api]);

  if (systems !== null && systems.length === 0) {
    return <Box color="text-body-secondary">No data systems are on offer yet.</Box>;
  }

  const byId = new Map((systems ?? []).map((s) => [s.systemId, s]));
  return (
    <SpaceBetween size="m">
      <FormField
        label="Data systems"
        description="Choose the systems your app will read from. After you submit, the platform team will set up a short call to agree exactly which tables and fields your app needs. Some data is by request and needs a clear business reason."
      >
        <Multiselect
          selectedOptions={selected.map((id) => ({ value: id, label: byId.get(id)?.displayName ?? id }))}
          options={(systems ?? []).map((s) => ({
            value: s.systemId,
            label: s.displayName,
            description: s.description,
            ...(s.hasRestricted ? { tags: ['Some data by request'] } : {}),
          }))}
          onChange={({ detail }) =>
            onChange(detail.selectedOptions.map((o) => o.value).filter((v): v is string => !!v), need)
          }
          placeholder={systems === null ? 'Loading data systems…' : 'None: my app does not use platform data'}
          filteringType="auto"
          statusType={systems === null ? 'loading' : 'finished'}
          ariaLabel="Data systems"
        />
      </FormField>
      {selected.length > 0 && (
        <FormField label="What does the app need the data for?" errorText={dataSectionError(selected, need)}>
          <Textarea value={need} onChange={({ detail }) => onChange(selected, detail.value)} rows={3} ariaLabel="What the app needs the data for" />
        </FormField>
      )}
    </SpaceBetween>
  );
}
